import { useCallback, useEffect, useState } from 'react';

import { isNumber, isString } from '@xemplo/common-util';

import { StatefulListInputProps } from '../types/stateful-list.types';

export function useTriggerWidth({ width, triggerId }: StatefulListInputProps) {
  const [computedWidth, setComputedWidth] = useState<string | number>();

  const getTriggerElement = useCallback(() => {
    if (typeof document === 'undefined' || !triggerId) return null;
    return document.getElementById(triggerId);
  }, [triggerId]);

  const computeWidth = useCallback(() => {
    // If the width is already a number, return it directly.
    if (isNumber(width)) return width;

    // If the width is a string, we can parse it to a number.
    // If it is a valid number, return it; otherwise, we will try to get
    // the width from the trigger element.
    if (isString(width)) {
      const parsed = Number(width.trim());
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }

    // If we reach here, we will try to get the width from the trigger element.
    // If the element is not found or its width is 0, we will return the
    // width prop as a fallback. This should also cover cases where the triggerId is not set.
    // but the width prop is provided as 100%, auto, fit-content, etc.
    const el = getTriggerElement();
    if (el) {
      const w = el.getBoundingClientRect().width;
      return w > 0 ? w : width;
    }

    return width;
  }, [width, getTriggerElement]);

  useEffect(() => {
    const newWidth = computeWidth();
    setComputedWidth(newWidth);

    const el = getTriggerElement();
    if (!el) return;

    // Using ResizeObserver to watch for changes
    // in the width of the trigger element.
    const observer = new ResizeObserver(() => {
      const updated = el.getBoundingClientRect().width;
      setComputedWidth(updated > 0 ? updated : undefined);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [computeWidth, getTriggerElement]);

  return computedWidth;
}
